import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowLeft, Loader2, MessageCircle } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import type { ChatBlock, ChatConversation } from '@/lib/sdk-official/resources/chat'
import { useChat } from './ChatProvider'
import { SCROLL_CLASSES } from './ChatConversationView'
import { ChatSettingsMenu } from './ChatSettingsMenu'
import { UnreadCount } from './UnreadCount'
import { PresenceDot } from './ChatStatus'
import { RoleChip, TEAM_RING_CLASSES, teamRoleOf } from './ChatTeam'
import { InfiniteSentinel } from '@/components/ui/infinite-sentinel'
import { displayPersonName } from '@/lib/personName'


const RowSkeleton = () => (
  <div className="flex items-center gap-2.5 px-3 py-2" aria-hidden>
    <div className="h-9 w-9 animate-pulse rounded-full bg-gray-100" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
      <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
    </div>
  </div>
)

const StateBlock = ({ icon: Icon, title, body, action }: { icon: React.ElementType; title: string; body?: string; action?: React.ReactNode }) => (
  <div className="flex flex-col items-center px-6 py-10 text-center">
    <Icon className="mb-2 h-8 w-8 text-gray-300" />
    <p className="text-sm font-semibold text-gray-900">{title}</p>
    {body && <p className="mt-1 text-xs text-gray-500">{body}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
)

const ConversationRow = ({ conversation, onOpen, showCount }: { conversation: ChatConversation; onOpen: () => void; showCount: boolean }) => {
  const { t, i18n } = useTranslation('chat')
  const other = conversation.other
  const name = displayPersonName(t, other?.name)
  const last = conversation.lastMessage
  const preview = !last ? '' : last.deleted ? t('conversations.deleted') : last.mine ? t('conversations.you', { text: last.body }) : last.body || ''
  const unread = showCount ? conversation.unreadCount || 0 : 0
  const role = teamRoleOf(other)
  const at = conversation.lastMessageAt
    ? new Intl.DateTimeFormat(i18n.language, new Date(conversation.lastMessageAt).toDateString() === new Date().toDateString()
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: 'short' }).format(new Date(conversation.lastMessageAt))
    : ''
  return (
    <button type="button" onClick={onOpen} className="flex min-h-[56px] w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50">
      <div className="relative shrink-0">
        <Avatar src={other?.avatar} alt={name} fallback={other?.name} className={cn('h-9 w-9', role && TEAM_RING_CLASSES)} />
        {other && <PresenceDot always online={other.online} status={other.status} className="-bottom-px -right-px h-2.5 w-2.5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn('truncate text-sm text-gray-900', unread ? 'font-semibold' : 'font-medium')}>{name}</span>
          <RoleChip role={role} />
          <span className="ml-auto shrink-0 text-[11px] text-gray-400">{at}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn('truncate text-xs', unread ? 'font-medium text-gray-800' : 'text-gray-500', last?.deleted && 'italic')}>{preview}</span>
          <UnreadCount count={unread} className="ml-auto" />
        </div>
      </div>
    </button>
  )
}

/** The blocked people list, opened from the ⋯ menu ("Pessoas bloqueadas"). */
export const BlockedPeople = ({ onBack }: { onBack: () => void }) => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const [blocks, setBlocks] = React.useState<ChatBlock[] | null>(null)
  const [state, setState] = React.useState<'loading' | 'ready' | 'failed'>('loading')
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setState('loading')
    try {
      const page = await sdk.chat.listBlocks()
      setBlocks(page.items)
      setState('ready')
    } catch {
      setState('failed')
    }
  }, [])
  React.useEffect(() => { load() }, [load])

  const unblock = async (profileId: string) => {
    setError(null)
    try {
      await chat.setBlocked(profileId, false)
      setBlocks((list) => (list || []).filter((b) => b.profile.id !== profileId))
    } catch {
      setError(t('errors.generic'))
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack} aria-label={t('back')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-semibold text-gray-900">{t('blocks.title')}</span>
      </div>
      <div className={cn('min-h-0 flex-1 overflow-y-auto', SCROLL_CLASSES)}>
        <div className="px-3 py-2.5">
          {state === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          {state === 'failed' && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <AlertTriangle className="h-4 w-4 text-amber-500" />{t('blocks.loadError')}
              <Button size="sm" variant="outline" onClick={load}>{t('link.retry')}</Button>
            </div>
          )}
          {state === 'ready' && !blocks?.length && <p className="text-xs text-gray-500">{t('blocks.empty')}</p>}
          {state === 'ready' && blocks?.map((b) => (
            <div key={b.profile.id} className="flex items-center gap-2 py-1.5">
              <Avatar src={b.profile.avatar} alt={displayPersonName(t, b.profile.name)} fallback={b.profile.name} size="sm" />
              <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{b.profile.name}</span>
              <Button size="sm" variant="outline" onClick={() => unblock(b.profile.id)}>{t('blocks.unblock')}</Button>
            </div>
          ))}
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  )
}

interface Props {
  /** Called when a conversation should be shown (the host decides where). */
  onOpenConversation: (conversationId: string) => void
  /** The host carries the ⋯ settings menu in its own header. */
  hideStatus?: boolean
  /** Hosts with their own ⋯ menu open the blocked people list here. */
  blocksOpen?: boolean
  onCloseBlocks?: () => void
  /** Filters the conversations by the other person's name (the desktop dock's search). */
  query?: string
}

/** The conversations panel (LinkedIn-style inbox): my conversations, settings in the ⋯ menu. "Pessoas" is a separate panel. */
export const ChatHome = ({ onOpenConversation, hideStatus = false, query = '', blocksOpen, onCloseBlocks }: Props) => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const [ownBlocks, setOwnBlocks] = React.useState(false)
  const available = Boolean(chat.me?.chatAvailable)
  /* "Indisponível" (or the badge turned off): no per-person counts. */
  const showCounts = available && chat.me?.showUnreadBadge !== false

  if (blocksOpen || ownBlocks) return <BlockedPeople onBack={() => { setOwnBlocks(false); onCloseBlocks?.() }} />

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={cn('min-h-0 flex-1 overflow-y-auto', SCROLL_CLASSES)}>
        {/* Inside a panel with the Conversas | Pessoas tabs, the label would repeat the tab; that host has the ⋯ menu. */}
        {!hideStatus && (
          <div className="flex items-center justify-between gap-2 px-3 pb-1 pt-2.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('tabs.conversations')}</p>
            <ChatSettingsMenu onOpenBlocks={() => setOwnBlocks(true)} />
          </div>
        )}
        {chat.conversationsState === 'loading' && chat.conversations.length === 0 && [0, 1, 2].map((i) => <RowSkeleton key={i} />)}
        {chat.conversationsState === 'failed' && chat.conversations.length === 0 && (
          <StateBlock
            icon={AlertTriangle}
            title={t('conversations.loadError')}
            action={<Button size="sm" variant="outline" onClick={chat.refreshConversations}>{t('link.retry')}</Button>}
          />
        )}
        {chat.conversationsState === 'ready' && chat.conversations.length === 0 && (
          <StateBlock
            icon={MessageCircle}
            title={t('conversations.emptyTitle')}
            body={available ? t('conversations.emptyAvailable') : t('conversations.emptyUnavailable')}
          />
        )}
        {chat.conversations.filter((c) => !query.trim() || (c.other?.name || '').toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())).map((c) => (
          <ConversationRow key={c.id} conversation={c} showCount={showCounts} onOpen={() => onOpenConversation(c.id)} />
        ))}
        <InfiniteSentinel hasMore={chat.conversationsHasMore} loading={chat.loadingMoreConversations} onLoadMore={chat.loadMoreConversations} count={chat.conversations.length} />
      </div>
    </div>
  )
}
